import { useMemo } from 'react';

import { useDirectoryGetRecords, useDirectoryGetSchemas } from 'api/requests';
import { useCountries } from 'api/requests/country';
import { PermissionType } from 'api/types/entity';
import { convertDirectoryFieldValueToLabel } from 'helpers';
import { useQueryListData } from 'hooks';
import { useTranslation } from 'libs/i18n';
import { generatePath, ROUTES, useNavigate } from 'libs/navigation';
import { usePermissions } from 'libs/permissions';
import { TableSetRowHref } from 'libs/ui';
import { getDirectoryFieldValue } from 'modules/directory/common/helpers';
import { useDirectoryRecordArchive } from 'modules/directory/common/hooks';

import { DirectoryRecordItemType } from '../types';

const LIMIT = 50;

export const useDirectory = (schemaId: string) => {
  const { t, language } = useTranslation();
  const { countries } = useCountries();

  const { navigate } = useNavigate();

  const { checkPermissions } = usePermissions();

  const {
    list: records,
    loading: loadingRecords,
    filter,
    setFilter,
    rowCount,
    page,
    onPaginationChange,
    serverError,
  } = useQueryListData(useDirectoryGetRecords, {
    limit: LIMIT,
    params: {
      directorySchemaName: schemaId,
    },
    filterParams: ['search'],
    filterParsers: {
      search: (value) => value,
    },
  });

  const { schemas: allSchemas, loading: loadingSchemas } =
    useDirectoryGetSchemas();

  const { onArchiveRecord, onDearchiveRecord } = useDirectoryRecordArchive();

  const schema = allSchemas?.find((schema) => schema.name === schemaId);

  const columns = useMemo(
    () =>
      schema?.fieldDefinitions.flatMap((field) => {
        if (field.isMultiLanguage) {
          return [
            {
              field: `${field.name}.ru`,
              title: `${field.label[language]} (RU)`,
            },
            {
              field: `${field.name}.en`,
              title: `${field.label[language]} (EN)`,
            },
          ];
        }
        return [
          {
            field: field.name,
            title: field.label[language] || field.label.ru,
          },
        ];
      }) ?? [],
    [schema, language]
  );

  const values = useMemo(() => {
    if (!schema || !allSchemas || !records) {
      return [];
    }
    const result: DirectoryRecordItemType[] = records?.map((record) => ({
      id: record.id,
      isArchived: record.isArchived,
      isArchivable: record.isArchivable,
      versionNumber: record.versionNumber,
      ...record.fields.reduce((acc, field) => {
        let value = getDirectoryFieldValue(field, schema, allSchemas);
        const fieldSchema = schema.fieldDefinitions.find(
          (f) => f.name === field.name
        );

        if (fieldSchema?.component === 'Country' && countries) {
          value =
            countries.find((country) => country.id === value)?.name ?? value;
        }

        if (fieldSchema?.isMultiLanguage) {
          acc[`${field.name}.ru`] = convertDirectoryFieldValueToLabel({
            t,
            language: 'ru',
            fieldValue: value,
          });
          acc[`${field.name}.en`] = convertDirectoryFieldValueToLabel({
            t,
            language: 'en',
            fieldValue: value,
          });
        } else {
          acc[field.name] = convertDirectoryFieldValueToLabel({
            t,
            language,
            fieldValue: value,
          });
        }

        return acc;
      }, {} as Record<string, string | number | boolean | null>),
    }));

    return result;
  }, [schema, allSchemas, records, countries, t, language]);

  const setRowHref: TableSetRowHref<any> | undefined = (client) =>
    generatePath(ROUTES.directories.directory.directoryRecord.fullPath, {
      schemaId,
      directoryRecordId: client.id,
    });

  const onNavigateAddRecord = checkPermissions([
    PermissionType.Directory_UpdateOne,
  ])
    ? () => {
        navigate(ROUTES.directories.directory.createDirectoryRecord.path, {
          schemaId,
        });
      }
    : undefined;

  return {
    title: schema?.label[language] || schema?.label.ru,
    loadingRecords: !records.length && loadingRecords,
    loadingSchemas: !allSchemas && loadingSchemas,
    columns,
    values,
    schema,
    allSchemas,
    rowCount,
    page,
    limit: LIMIT,
    filter,
    notFound: !!serverError,
    setFilter,
    setRowHref,
    onArchiveRecord,
    onDearchiveRecord,
    onPaginationChange,
    onNavigateAddRecord,
  };
};
